import { Box, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text, useBreakpointValue, useColorModeValue, useToast, VStack } from "@chakra-ui/react";
import { useRef, useState } from "react";

import { $length, $ok } from "foundation-ts/commons";
import { email, Nullable } from "foundation-ts/types";

import { ModalWindowState } from "@components/Modals/ModalConstants";
import { localtrans } from "@/utils/LocalTranslations";
import { defaultInfoToast } from "@/utils/toast";

interface AdminOTPModalState extends ModalWindowState {
    isOpen: boolean;
    userEmail?: email;
    onSendOTP: () => Promise<boolean>;
    onAuthenticateWithOTP: (otp: string) => Promise<boolean>;
}

export const AdminOTPModal = (props: AdminOTPModalState) => {
    const { isOpen, userEmail, onClose, onSendOTP, onAuthenticateWithOTP } = props;
    const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
    const [isVerifying, setIsVerifying] = useState(false);
    const [errorText, setErrorText] = useState<Nullable<string>>(null);
    const inputRefs = useRef<Nullable<HTMLInputElement>[]>([]);
    const toast = useToast();
    const textColor = useColorModeValue("gray.600", "gray.400");
    const modalSize = useBreakpointValue({ base: "sm", lg: "lg" });

    const resetModal = () => {
        setOtpValues(['', '', '', '', '', '']);
        setIsVerifying(false);
    };

    const signinWithOTP = async (otp: string) => {
        setIsVerifying(true);
        const ret = await onAuthenticateWithOTP(otp);
        if (ret) {
            handleClose();
        } else {
            setIsVerifying(false);
            setErrorText("Vous avez saisi un code OTP invalide");
        }
    }

    const handleClose = async () => {
        resetModal();
        await onClose();
    };

    const handleOTPChange = async (index: number, value: string) => {
        const numericValue = value.replace(/[^0-9]/g, '');
        if ($length(numericValue) <= 1) {
            const newOtpValues = [...otpValues];
            newOtpValues[index] = numericValue;
            setOtpValues(newOtpValues);
            setErrorText(null);

            if (numericValue && index < 5) {
                inputRefs.current[index + 1]?.focus();
            } else if (numericValue && index === 5) {
                const completeOtp = [...newOtpValues.slice(0, 5), numericValue].join('');
                await signinWithOTP(completeOtp);
            }
        }
    }

    const handleOTPKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    }

    const handleOTPPaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
        if ($length(pastedData) === 6) {
            const newOtpValues = pastedData.split('');
            setOtpValues(newOtpValues);
            inputRefs.current[5]?.focus();
            await signinWithOTP(pastedData);
        }
    }

    const handleResendOTP = async () => {
        setOtpValues(['', '', '', '', '', '']);
        const success = await onSendOTP();
        if (success) {
            toast(defaultInfoToast(`Un nouveau code OTP a été envoyé à l'adresse ${userEmail}`));
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size={modalSize} isCentered >
            <ModalOverlay backdropFilter="blur(6px)" />
            <ModalContent> 
                <ModalHeader fontSize={["md","lg"]}>{localtrans("SignatureModal.ConfirmOTP")}</ModalHeader>
                <ModalCloseButton />

                <ModalBody pb={4}>
                    <VStack spacing={4} align="stretch">
                        <Text color={textColor} textAlign="center" fontSize="md">
                            {localtrans("SignatureModal.SentCode")}
                            <br/>
                            <Text as="span" fontWeight="bold">{userEmail}</Text>
                        </Text>

                        <Box display="flex" justifyContent="center" gap={2}>
                            {otpValues.map((value, index) => (
                                <Input key={index} type="text" ref={(el) => (inputRefs.current[index] = el)} inputMode="numeric" maxLength={1} value={value} disabled={isVerifying}
                                       textAlign="center" fontSize="2xl" fontWeight="bold" w="50px" h="60px" borderWidth="2px" borderColor={value ? 'blue.400' : 'gray.200'}
                                       _focus={{
                                            borderColor: 'blue.500',
                                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
                                       }}
                                       onChange={(e) => handleOTPChange(index, e.target.value)}
                                       onKeyDown={(e) => handleOTPKeyDown(index, e)}
                                       onPaste={index === 0 ? (e) => handleOTPPaste(e) : undefined}
                                />
                            ))}
                        </Box>
                        { $ok(errorText) && <Text fontSize={["sm", "md"]} color="red" textAlign="center">{errorText}</Text> }
                        <Text fontSize={["sm", "md"]} color={textColor} textAlign="center">
                            {localtrans("SignatureModal.NotReceivedOTP")}
                            <Text as="button" color="blue.500" fontWeight="semibold" _hover={{ textDecoration: 'underline' }} disabled={isVerifying} onClick={handleResendOTP}>
                                {localtrans("SignatureModal.ResendOTP")}
                            </Text>
                        </Text>
                    </VStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};